// src/app/ai-spreadsheet/client.tsx
"use client";

import React, { useMemo, useState, useTransition } from "react";
import type { TableData, GenerateArgs } from "./types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Textarea } from "@/app/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Badge } from "@/app/components/ui/badge";
import { Download, Loader2, Sparkles, Copy, Plus, Minus, Table as TableIcon } from "lucide-react";
import { toast } from "react-hot-toast";
import { saveAs } from "file-saver";

export default function ClientSheet({ generate }: { generate: (args: GenerateArgs) => Promise<TableData> }) {
  const [prompt, setPrompt] = useState("");
  const [rows, setRows] = useState(20);
  const [cols, setCols] = useState(0);
  const [headersText, setHeadersText] = useState("");
  const [provider, setProvider] = useState<GenerateArgs["provider"]>("auto");
  const [seed, setSeed] = useState<number>(Math.floor(Math.random() * 100000));

  const [data, setData] = useState<TableData | null>(null);
  const [isPending, startTransition] = useTransition();

  const canGenerate = prompt.trim().length > 0 || headersText.trim().length > 0;

  const handleGenerate = () => {
    if (!canGenerate) {
      toast.error("Give me a prompt or headers to work with.");
      return;
    }
    startTransition(async () => {
      try {
        const res = await generate({ prompt, rows, cols, headersText, provider, seed });
        setData(res);
        
        // More informative toast based on what actually happened
        if (res.meta?.from === "ai") {
          toast.success(`AI table ready (${res.meta.provider.toUpperCase()})`);
        } else if (res.meta?.error) {
          if (res.meta.error.includes("Rate limit")) {
            toast.error("Rate limit hit - using local fallback", {
              duration: 5000,
              icon: "⚠️"
            });
          } else {
            toast.error(`Fallback used: ${res.meta.error}`);
          }
        } else {
          toast.success("Generated locally");
        }
      } catch (e: any) {
        toast.error(e?.message || "Generation failed");
      }
    });
  };

  const csv = useMemo(() => (data ? tableToCSV(data) : ""), [data]);

  const copyCSV = async () => {
    if (!data) return;
    await navigator.clipboard.writeText(csv);
    toast.success("CSV copied to clipboard");
  };

  const downloadCSV = () => {
    if (!data) return;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const nameBase = slugify(prompt || "ai-spreadsheet");
    saveAs(blob, `${nameBase}.csv`);
  };

  const addRow = () => {
    if (!data) return;
    const empty = Array.from({ length: data.headers.length }, () => "");
    setData({ ...data, rows: [...data.rows, empty] });
  };

  const removeRow = () => {
    if (!data) return;
    if (data.rows.length === 0) return;
    setData({ ...data, rows: data.rows.slice(0, -1) });
  };

  const addCol = () => {
    if (!data) return;
    const idx = data.headers.length + 1;
    const headers = [...data.headers, `Column ${idx}`];
    const rows = data.rows.map((r) => [...r, ""]);
    setData({ headers, rows });
  };

  const removeCol = () => {
    if (!data) return;
    if (data.headers.length === 0) return;
    const headers = data.headers.slice(0, -1);
    const rows = data.rows.map((r) => r.slice(0, -1));
    setData({ headers, rows });
  };

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold tracking-tight flex items-center gap-2 justify-center">
            <TableIcon className="h-8 w-8" /> AI Spreadsheet Maker
          </h1>
          <p className="text-muted-foreground mt-2">
            Type what you want. Get a clean, editable table. Export as CSV like a pro.
          </p>
        </div>

        <Card className="card-enhanced mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" /> Describe your spreadsheet
            </CardTitle>
            <CardDescription>
              Example: "A monthly budget with Category, Planned, Actual, Difference for 12 rows"
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="prompt">Prompt</Label>
                <Textarea
                  id="prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Sales pipeline with Stage, Owner, Deal Size, Probability, Close Date…"
                  rows={5}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rows (target)</Label>
                  <Input type="number" min={1} max={500} value={rows} onChange={(e) => setRows(Number(e.target.value))} />
                </div>
                <div className="space-y-2">
                  <Label>Columns (optional)</Label>
                  <Input type="number" min={0} max={50} value={cols} onChange={(e) => setCols(Number(e.target.value))} />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Explicit headers (comma or newline separated, optional)</Label>
                  <Textarea
                    value={headersText}
                    onChange={(e) => setHeadersText(e.target.value)}
                    placeholder={`Name, Email, Company, Plan\n# or one per line`}
                    rows={4}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select value={provider} onValueChange={(v) => setProvider(v as any)}>
                    <SelectTrigger><SelectValue placeholder="Choose provider" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="gemini">Gemini</SelectItem>
                      <SelectItem value="none">No AI (local)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Seed (consistency)</Label>
                  <Input type="number" value={seed} onChange={(e) => setSeed(Number(e.target.value) || 0)} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button className="btn-enhanced hover-lift" onClick={handleGenerate} disabled={isPending || !canGenerate}>
                {isPending ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Thinking…</>) : (<><Sparkles className="h-4 w-4 mr-2" /> Generate</>)}
              </Button>
              <Badge variant={provider === "none" ? "destructive" : "default"}>
                {provider === "none" ? "Local (no API key)" : provider === "auto" ? "Auto provider" : provider?.toUpperCase()}
              </Badge>
            </div>
            
            {/* Status line showing what actually happened */}
            {data?.meta && (
              <div className="text-sm text-muted-foreground border-t pt-3">
                <span className="font-medium">Used:</span>{" "}
                <span className="font-semibold text-primary">{data.meta.provider.toUpperCase()}</span>{" "}
                → {data.meta.from === "ai" ? (
                  <span className="text-green-600 font-medium">AI ({data.meta.aiModel})</span>
                ) : (
                  <span className="text-amber-600 font-medium">Local fallback</span>
                )}
                {data.meta.error && (
                  <span className="ml-2 text-red-500 text-xs">({data.meta.error})</span>
                )}
                
                                 {/* Additional AI metrics in status line */}
                 {data.meta.from === "ai" && (data.meta.tokensUsed || data.meta.cost) && (
                   <div className="mt-1 text-xs">
                     {data.meta.tokensUsed && (
                       <span className="mr-3">Tokens: {data.meta.tokensUsed.toLocaleString()}</span>
                     )}
                     {data.meta.cost && (
                       <span>Cost: ${data.meta.cost.toFixed(4)}</span>
                     )}
                   </div>
                 )}
                 
                 {/* Helpful suggestions for common errors in status line */}
                 {data.meta.error && (
                   <div className="mt-1 text-xs">
                     {data.meta.error.includes("Rate limit") && (
                       <span className="text-amber-600">💡 Wait a few minutes or try different provider</span>
                     )}
                     {data.meta.error.includes("API key") && (
                       <span className="text-red-600">🔑 Check API key configuration</span>
                     )}
                     {data.meta.error.includes("temporarily unavailable") && (
                       <span className="text-blue-600">⏳ Try again later</span>
                     )}
                   </div>
                 )}
              </div>
            )}
          </CardContent>
        </Card>

        {data && (
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle>Preview & Edit</CardTitle>
              <CardDescription>Double-click a cell to edit. Add/remove rows or columns on the fly.</CardDescription>
              
              {/* Prominent status display */}
              {data.meta && (
                <div className="mt-3 p-3 bg-muted/50 rounded-lg border">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">Generation Status:</span>
                    <Badge variant={data.meta.from === "ai" ? "default" : "secondary"}>
                      {data.meta.provider.toUpperCase()}
                    </Badge>
                    <span className="text-muted-foreground">→</span>
                    <Badge variant={data.meta.from === "ai" ? "default" : "destructive"}>
                      {data.meta.from === "ai" ? `AI (${data.meta.aiModel})` : "Local Fallback"}
                    </Badge>
                    {data.meta.error && (
                      <span className="text-red-500 text-xs ml-2">({data.meta.error})</span>
                    )}
                  </div>
                  
                                     {/* Additional AI metrics */}
                   {data.meta.from === "ai" && (data.meta.tokensUsed || data.meta.cost) && (
                     <div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
                       {data.meta.tokensUsed && (
                         <span className="mr-3">Tokens: {data.meta.tokensUsed.toLocaleString()}</span>
                       )}
                       {data.meta.cost && (
                         <span>Cost: ${data.meta.cost.toFixed(4)}</span>
                       )}
                     </div>
                   )}
                   
                   {/* Helpful suggestions for common errors */}
                   {data.meta.error && (
                     <div className="mt-2 pt-2 border-t text-xs">
                       {data.meta.error.includes("Rate limit") && (
                         <div className="text-amber-600">
                           💡 <strong>Tip:</strong> Wait a few minutes before trying again, or switch to a different provider
                           <div className="mt-1 flex gap-2">
                             <Button 
                               variant="outline" 
                               size="sm" 
                               onClick={() => window.location.reload()}
                               className="text-xs h-6 px-2"
                             >
                               🔄 Retry Now
                             </Button>
                             <Button 
                               variant="outline" 
                               size="sm" 
                               onClick={() => setProvider("openai")}
                               className="text-xs h-6 px-2"
                             >
                               🔀 Try OpenAI
                             </Button>
                           </div>
                         </div>
                       )}
                       {data.meta.error.includes("API key") && (
                         <div className="text-red-600">
                           🔑 <strong>Action needed:</strong> Check your API key configuration
                         </div>
                       )}
                       {data.meta.error.includes("temporarily unavailable") && (
                         <div className="text-blue-600">
                           ⏳ <strong>Status:</strong> Service is experiencing issues, try again later
                         </div>
                       )}
                     </div>
                   )}
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={addRow}><Plus className="h-3 w-3 mr-1" /> Row</Button>
                <Button variant="outline" size="sm" onClick={removeRow}><Minus className="h-3 w-3 mr-1" /> Row</Button>
                <Button variant="outline" size="sm" onClick={addCol}><Plus className="h-3 w-3 mr-1" /> Column</Button>
                <Button variant="outline" size="sm" onClick={removeCol}><Minus className="h-3 w-3 mr-1" /> Column</Button>
                <div className="ml-auto flex gap-2">
                  <Button variant="outline" size="sm" onClick={copyCSV}><Copy className="h-3 w-3 mr-1" /> Copy CSV</Button>
                  <Button size="sm" className="btn-enhanced hover-lift" onClick={downloadCSV}><Download className="h-3 w-3 mr-1" /> Download CSV</Button>
                </div>
              </div>

              <div className="w-full overflow-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      {data.headers.map((h, i) => (
                        <th key={i} className="px-3 py-2 text-left font-semibold border-b whitespace-nowrap align-bottom">
                          <InlineEditable
                            value={h}
                            onChange={(v) => {
                              const headers = [...data.headers];
                              headers[i] = v;
                              setData({ ...data, headers });
                            }}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, rIdx) => (
                      <tr key={rIdx} className="even:bg-muted/40">
                        {row.map((cell, cIdx) => (
                          <td key={cIdx} className="px-3 py-2 border-b align-top">
                            <InlineEditable
                              value={cell}
                              onChange={(v) => {
                                const rows = data.rows.map((r, ri) => (ri === rIdx ? r.map((c, ci) => (ci === cIdx ? v : c)) : r));
                                setData({ ...data, rows });
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function InlineEditable({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  React.useEffect(() => setVal(value), [value]);

  return (
    <div
      className={`rounded px-1 ${editing ? "ring-2 ring-primary" : "hover:bg-muted cursor-text"}`}
      onDoubleClick={() => setEditing(true)}
    >
      {editing ? (
        <Input
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={() => {
            setEditing(false);
            onChange(val);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
      ) : (
        <span>{value || <span className="text-muted-foreground">(empty)</span>}</span>
      )}
    </div>
  );
}

// client helpers
function tableToCSV(t: TableData): string {
  const esc = (s: string) => {
    if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const lines = [t.headers.map(esc).join(","), ...t.rows.map((r) => r.map(esc).join(","))];
  return lines.join("\n");
}

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)+/g, "").slice(0, 80) || "sheet";
}
